import type { To } from 'react-router';
import { merge } from 'lodash-es';
import React, { useId } from 'react';
import { useLocation } from 'react-router';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { VariantProp } from '../../common/colors';
import type { TooltipProps } from '../../components/Tooltip/Tooltip';
import type { WrapProps } from '../../utilities/shared/Wrap';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { fade, palette } from '../../common/colors';
import { Link } from '../../components/Link/Link';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { selectors, transitions } from '../../controls/shared/styles';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { displayCSS } from '../../utilities/shared/Display';
import { wrapCSS } from '../../utilities/shared/Wrap';

const DeviceTargetIcon = styled(DynamicIcon, {
  color: '$$iconColor',
});

const DeviceTargetAnnotation = styled(Text, {
  ...SmallSansSizes,

  display: 'flex',
  padding: '$$annotationPadding',
  borderRadius: '$$annotationRadius',
  background: '$$annotationBackground',
  color: '$$annotationColor',
  whiteSpace: 'nowrap',
});

const DeviceTargetLabel = styled(Text, {
  color: '$$labelColor',

  variants: {
    size: {
      large: {
        gap: '$6',
      },
      medium: {
        gap: '$4',
      },
      small: {
        gap: '$4',
      },
    },
    wrap: {
      nowrap: {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      wrap: {
        wordBreak: 'break-word',
        whiteSpace: 'pre-line',
      },
    },
  },
});

const DeviceTargetAnnotations = styled(Text, {
  display: 'flex',
  flexDirection: 'row',
  gap: '$4',
});

const DeviceTargetContents = styled(AlignStack, {
  position: 'relative',
  zIndex: 2,
});

const DeviceTargetBoundary = styled('div', {
  position: 'absolute',
  zIndex: 1,
  top: '$$boundaryEnds',
  right: '$$boundarySides',
  bottom: '$$boundaryEnds',
  left: '$$boundarySides',
  background: '$$boundaryColor',
  strokeAll: '$$boundaryStroke',
  borderRadius: '$$boundaryRadius',
  transition: transitions.control,
});

const DeviceTargetContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  maxWidth: '100%',
  verticalAlign: 'bottom',
  transition: transitions.control,

  variants: {
    isPressable: {
      true: {
        cursor: 'pointer',

        [selectors.focus]: {
          outline: 'none',

          [`${DeviceTargetBoundary}`]: {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {
        cursor: 'default',
        $$iconColor: colors.iconNeutralLight,
        $$labelColor: colors.bodyNeutralLight,
        $$annotationBackground: fade(palette.tokenBgNeutralLight, 0.4),
        $$annotationColor: colors.bodyNeutralLight,

        [darkThemeSelector]: {
          $$iconColor: colors.iconNeutralDark,
          $$labelColor: colors.bodyNeutralDark,
          $$annotationBackground: fade(palette.tokenBgNeutralDark, 0.3),
          $$annotationColor: colors.bodyNeutralDark,
        },

        [selectors.hover]: {
          $$boundaryColor: fade(palette.tokenBgNeutralLight, 0.4),
          $$boundaryStroke: fade(palette.tokenStrokeNeutralLight, 0.5),
          $$iconColor: colors.bodyNeutralLight,
          $$labelColor: colors.headingNeutralLight,

          [darkThemeSelector]: {
            $$boundaryColor: fade(palette.tokenBgNeutralDark, 0.3),
            $$boundaryStroke: fade(palette.tokenStrokeNeutralDark, 0.4),
            $$iconColor: colors.bodyNeutralDark,
            $$labelColor: colors.headingNeutralDark,
          },
        },
      },
    },
    selected: {
      true: {
        $$boundaryColor: fade(palette.bgBrandLight, 0.8),
        $$boundaryStroke: colors.strokeBrandLight,
        $$iconColor: colors.bodyBrandLight,
        $$labelColor: colors.headingBrandLight,
        $$annotationBackground: fade(palette.bgBrandLight, 0.5),
        $$annotationColor: colors.bodyBrandLight,

        [darkThemeSelector]: {
          $$boundaryColor: fade(palette.bgBrandDark, 0.8),
          $$boundaryStroke: colors.strokeBrandDark,
          $$iconColor: colors.bodyBrandDark,
          $$labelColor: colors.headingBrandDark,
          $$annotationBackground: fade(palette.bgBrandDark, 0.5),
          $$annotationColor: colors.bodyBrandDark,
        },
      },
      false: {},
    },
    size: {
      small: {
        display: 'inline-flex',
        $$boundaryEnds: '-$space$2',
        $$boundarySides: '-$space$4',
        $$boundaryRadius: '$space$6',
        $$annotationRadius: '$space$4',
        $$annotationPadding: '$space$1 $space$3',
      },
      medium: {
        $$boundaryEnds: '-$space$4',
        $$boundarySides: '-$space$6',
        $$boundaryRadius: '$space$8',
        $$annotationRadius: '$space$6',
        $$annotationPadding: '$space$2 $space$4',
      },
      large: {
        $$boundaryEnds: '-$space$4',
        $$boundarySides: '-$space$12',
        $$boundaryRadius: '$space$10',
        $$annotationRadius: '$space$8',
        $$annotationPadding: '$space$4 $space$6',
      },
    },
    variant: {
      alternative: {},
      attention: {},
      brand: {},
      negative: {},
      neutral: {},
      positive: {},
    },
  },

  compoundVariants: [
    {
      isPressable: true,
      variant: 'alternative',
      css: {
        $$iconColor: colors.linkAlternativeIconInitialLight,
        $$labelColor: colors.linkAlternativeInitialLight,
        $$annotationBackground: fade(palette.tokenBgAlternativeLight, 0.4),
        $$annotationColor: colors.bodyAlternativeLight,

        [darkThemeSelector]: {
          $$iconColor: colors.linkAlternativeIconInitialDark,
          $$labelColor: colors.linkAlternativeInitialDark,
          $$annotationBackground: fade(palette.tokenBgAlternativeDark, 0.4),
          $$annotationColor: colors.bodyAlternativeDark,
        },

        [selectors.hover]: {
          $$boundaryColor: fade(palette.tokenBgAlternativeLight, 0.4),
          $$boundaryStroke: fade(palette.tokenStrokeAlternativeLight, 0.5),
          $$iconColor: colors.bodyAlternativeLight,
          $$labelColor: colors.headingAlternativeLight,

          [darkThemeSelector]: {
            $$boundaryColor: fade(palette.tokenBgAlternativeDark, 0.3),
            $$boundaryStroke: fade(palette.tokenStrokeAlternativeDark, 0.4),
            $$iconColor: colors.bodyAlternativeDark,
            $$labelColor: colors.headingAlternativeDark,
          },
        },

        [selectors.focus]: {
          $$boundaryColor: colors.bgAlternativeLight,
          $$iconColor: colors.bodyAlternativeLight,
          $$labelColor: colors.headingAlternativeLight,

          [darkThemeSelector]: {
            $$boundaryColor: colors.bgAlternativeDark,
            $$iconColor: colors.bodyAlternativeDark,
            $$labelColor: colors.headingAlternativeDark,
          },
        },
      },
    },
    {
      isPressable: true,
      variant: 'attention',
      css: {
        $$iconColor: colors.linkAttentionIconInitialLight,
        $$labelColor: colors.linkAttentionInitialLight,
        $$annotationBackground: fade(palette.tokenBgAttentionLight, 0.4),
        $$annotationColor: colors.bodyAttentionLight,

        [darkThemeSelector]: {
          $$iconColor: colors.linkAttentionIconInitialDark,
          $$labelColor: colors.linkAttentionInitialDark,
          $$annotationBackground: fade(palette.tokenBgAttentionDark, 0.4),
          $$annotationColor: colors.bodyAttentionDark,
        },

        [selectors.hover]: {
          $$boundaryColor: fade(palette.tokenBgAttentionLight, 0.4),
          $$boundaryStroke: fade(palette.tokenStrokeAttentionLight, 0.5),
          $$iconColor: colors.bodyAttentionLight,
          $$labelColor: colors.headingAttentionLight,

          [darkThemeSelector]: {
            $$boundaryColor: fade(palette.tokenBgAttentionDark, 0.3),
            $$boundaryStroke: fade(palette.tokenStrokeAttentionDark, 0.4),
            $$iconColor: colors.bodyAttentionDark,
            $$labelColor: colors.headingAttentionDark,
          },
        },

        [selectors.focus]: {
          $$boundaryColor: colors.bgAttentionLight,
          $$iconColor: colors.bodyAttentionLight,
          $$labelColor: colors.headingAttentionLight,

          [darkThemeSelector]: {
            $$boundaryColor: colors.bgAttentionDark,
            $$iconColor: colors.bodyAttentionDark,
            $$labelColor: colors.headingAttentionDark,
          },
        },
      },
    },
    {
      isPressable: true,
      variant: 'brand',
      css: {
        $$iconColor: colors.linkBrandIconInitialLight,
        $$labelColor: colors.linkBrandInitialLight,
        $$annotationBackground: fade(palette.tokenBgBrandLight, 0.4),
        $$annotationColor: colors.bodyBrandLight,

        [darkThemeSelector]: {
          $$iconColor: colors.linkBrandIconInitialDark,
          $$labelColor: colors.linkBrandInitialDark,
          $$annotationBackground: fade(palette.tokenBgBrandDark, 0.4),
          $$annotationColor: colors.bodyBrandDark,
        },

        [selectors.hover]: {
          $$boundaryColor: fade(palette.tokenBgBrandLight, 0.4),
          $$boundaryStroke: fade(palette.tokenStrokeBrandLight, 0.5),
          $$iconColor: colors.bodyBrandLight,
          $$labelColor: colors.headingBrandLight,

          [darkThemeSelector]: {
            $$boundaryColor: fade(palette.tokenBgBrandDark, 0.3),
            $$boundaryStroke: fade(palette.tokenStrokeBrandDark, 0.4),
            $$iconColor: colors.bodyBrandDark,
            $$labelColor: colors.headingBrandDark,
          },
        },

        [selectors.focus]: {
          $$boundaryColor: colors.bgBrandLight,
          $$iconColor: colors.bodyBrandLight,
          $$labelColor: colors.headingBrandLight,

          [darkThemeSelector]: {
            $$boundaryColor: colors.bgBrandDark,
            $$iconColor: colors.bodyBrandDark,
            $$labelColor: colors.headingBrandDark,
          },
        },
      },
    },
    {
      isPressable: true,
      variant: 'negative',
      css: {
        $$iconColor: colors.linkNegativeIconInitialLight,
        $$labelColor: colors.linkNegativeInitialLight,
        $$annotationBackground: fade(palette.tokenBgNegativeLight, 0.4),
        $$annotationColor: colors.bodyNegativeLight,

        [darkThemeSelector]: {
          $$iconColor: colors.linkNegativeIconInitialDark,
          $$labelColor: colors.linkNegativeInitialDark,
          $$annotationBackground: fade(palette.tokenBgNegativeDark, 0.4),
          $$annotationColor: colors.bodyNegativeDark,
        },

        [selectors.hover]: {
          $$boundaryColor: fade(palette.tokenBgNegativeLight, 0.4),
          $$boundaryStroke: fade(palette.tokenStrokeNegativeLight, 0.5),
          $$iconColor: colors.bodyNegativeLight,
          $$labelColor: colors.headingNegativeLight,

          [darkThemeSelector]: {
            $$boundaryColor: fade(palette.tokenBgNegativeDark, 0.3),
            $$boundaryStroke: fade(palette.tokenStrokeNegativeDark, 0.4),
            $$iconColor: colors.bodyNegativeDark,
            $$labelColor: colors.headingNegativeDark,
          },
        },

        [selectors.focus]: {
          $$boundaryColor: colors.bgNegativeLight,
          $$iconColor: colors.bodyNegativeLight,
          $$labelColor: colors.headingNegativeLight,

          [darkThemeSelector]: {
            $$boundaryColor: colors.bgNegativeDark,
            $$iconColor: colors.bodyNegativeDark,
            $$labelColor: colors.headingNegativeDark,
          },
        },
      },
    },
    {
      isPressable: true,
      variant: 'neutral',
      css: {
        $$iconColor: colors.linkNeutralIconInitialLight,
        $$labelColor: colors.linkNeutralInitialLight,
        $$annotationBackground: fade(palette.tokenBgNeutralLight, 0.4),
        $$annotationColor: colors.bodyNeutralLight,

        [darkThemeSelector]: {
          $$iconColor: colors.linkNeutralIconInitialDark,
          $$labelColor: colors.linkNeutralInitialDark,
          $$annotationBackground: fade(palette.tokenBgNeutralDark, 0.4),
          $$annotationColor: colors.bodyNeutralDark,
        },

        [selectors.hover]: {
          $$boundaryColor: fade(palette.tokenBgNeutralLight, 0.4),
          $$boundaryStroke: fade(palette.tokenStrokeNeutralLight, 0.5),
          $$iconColor: colors.bodyNeutralLight,
          $$labelColor: colors.headingNeutralLight,

          [darkThemeSelector]: {
            $$boundaryColor: fade(palette.tokenBgNeutralDark, 0.3),
            $$boundaryStroke: fade(palette.tokenStrokeNeutralDark, 0.4),
            $$iconColor: colors.bodyNeutralDark,
            $$labelColor: colors.headingNeutralDark,
          },
        },

        [selectors.focus]: {
          $$boundaryColor: colors.bgNeutralLight,
          $$iconColor: colors.bodyNeutralLight,
          $$labelColor: colors.headingNeutralLight,

          [darkThemeSelector]: {
            $$boundaryColor: colors.bgNeutralDark,
            $$iconColor: colors.bodyNeutralDark,
            $$labelColor: colors.headingNeutralDark,
          },
        },
      },
    },
    {
      isPressable: true,
      variant: 'positive',
      css: {
        $$iconColor: colors.linkPositiveIconInitialLight,
        $$labelColor: colors.linkPositiveInitialLight,
        $$annotationBackground: fade(palette.tokenBgPositiveLight, 0.4),
        $$annotationColor: colors.bodyPositiveLight,

        [darkThemeSelector]: {
          $$iconColor: colors.linkPositiveIconInitialDark,
          $$labelColor: colors.linkPositiveInitialDark,
          $$annotationBackground: fade(palette.tokenBgPositiveDark, 0.4),
          $$annotationColor: colors.bodyPositiveDark,
        },

        [selectors.hover]: {
          $$boundaryColor: fade(palette.tokenBgPositiveLight, 0.4),
          $$boundaryStroke: fade(palette.tokenStrokePositiveLight, 0.5),
          $$iconColor: colors.bodyPositiveLight,
          $$labelColor: colors.headingPositiveLight,

          [darkThemeSelector]: {
            $$boundaryColor: fade(palette.tokenBgPositiveDark, 0.3),
            $$boundaryStroke: fade(palette.tokenStrokePositiveDark, 0.4),
            $$iconColor: colors.bodyPositiveDark,
            $$labelColor: colors.headingPositiveDark,
          },
        },

        [selectors.focus]: {
          $$boundaryColor: colors.bgPositiveLight,
          $$iconColor: colors.bodyPositiveLight,
          $$labelColor: colors.headingPositiveLight,

          [darkThemeSelector]: {
            $$boundaryColor: colors.bgPositiveDark,
            $$iconColor: colors.bodyPositiveDark,
            $$labelColor: colors.headingPositiveDark,
          },
        },
      },
    },
  ],
});

export type DeviceTargetPropSize = 'large' | 'medium' | 'small';

export type DeviceTargetBaseProps = {
  annotation?: React.ReactNode | React.ReactNode[];
  'aria-label'?: string;
  children?: React.ReactNode;
  display?: 'block' | 'flex' | 'inline-block' | 'inline-flex';
  icon?: DynamicIconName;
  label?: React.ReactNode;
  onClick?: (event: any) => void;
  size?: DeviceTargetPropSize;
  selected?: boolean;
  tooltip?: TooltipProps;
  variant?: VariantProp;
  wrap?: 'nowrap' | 'wrap';
};

export type DeviceTargetProps<Tag extends React.ElementType = 'span'> = PolymorphicComponentProps<
  Tag,
  DeviceTargetBaseProps
>;

const getDeviceTargetIconSize = (size: DeviceTargetPropSize) => {
  switch (size) {
    case 'large':
      return 16;
    default:
      return 14;
  }
};

const getDeviceTargetPreset = (size: DeviceTargetPropSize) => {
  if (size === 'large') return 'large';
  if (size === 'medium') return 'body';
  return 'small';
};

export function DeviceTarget<Tag extends React.ElementType>({
  annotation,
  as = 'span' as Tag,
  children,
  display = 'flex',
  icon,
  label,
  onClick,
  selected,
  size = 'medium',
  tooltip,
  variant = 'alternative',
  wrap = 'wrap',
  ref,
  ...remaining
}: DeviceTargetProps<Tag>) {
  const isPressable = Boolean(onClick || (as !== 'span' && as !== 'div'));
  const css = displayCSS(display);

  const renderAnnotation = Array.isArray(annotation) ? (
    <DeviceTargetAnnotations>
      {annotation.map((item) => (
        <DeviceTargetAnnotation key={item as string}>{item}</DeviceTargetAnnotation>
      ))}
    </DeviceTargetAnnotations>
  ) : (
    <DeviceTargetAnnotation>{annotation}</DeviceTargetAnnotation>
  );

  const render = (
    <DeviceTargetContainer
      ref={ref}
      as={as}
      onClick={onClick}
      role={onClick && 'button'}
      tabIndex={isPressable ? 0 : undefined}
      isPressable={isPressable}
      size={size}
      selected={selected}
      variant={variant}
      css={css}
      {...remaining}
    >
      <DeviceTargetContents
        direction="row"
        gap={size === 'large' ? 6 : 4}
        preset={getDeviceTargetPreset(size)}
        start={icon && <DeviceTargetIcon size={getDeviceTargetIconSize(size)} icon={icon} />}
        end={annotation && renderAnnotation}
      >
        {(children != null || label != null) && (
          <DeviceTargetLabel size={size} wrap={wrap}>
            {children ?? label}
          </DeviceTargetLabel>
        )}
      </DeviceTargetContents>
      <DeviceTargetBoundary />
    </DeviceTargetContainer>
  );

  return tooltip ? <Tooltip {...tooltip}>{render}</Tooltip> : render;
}

const DeviceTargetsToken = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'no-wrap',
  alignItems: 'center',
});

const DeviceTargetsContainer = styled('div', {
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  verticalAlign: 'bottom',
  gap: '$6',
});

function DeviceTargetsOverflow({
  targets,
  maximumTargets,
  overflow,
}: {
  targets: DeviceTargetProps<any>[];
  maximumTargets?: number;
  overflow?: {
    as?: React.ElementType;
    to?: To;
  };
}) {
  const overflowCount = maximumTargets ? targets.length - maximumTargets : undefined;
  const location = useLocation();
  const isCurrentPath = overflow?.to && location.pathname === overflow.to;

  return (
    <Text>
      and{' '}
      {overflow && !isCurrentPath ? (
        <Link as={overflow?.as} to={overflow?.to}>
          {overflowCount} more
        </Link>
      ) : (
        <Text>{overflowCount} more</Text>
      )}
    </Text>
  );
}

export function DeviceTargets<Tag extends React.ElementType>({
  display = 'flex',
  hideSeparator = false,
  maximumTargets,
  overflow,
  size = 'medium',
  targets,
  variant = 'alternative',
  wrap = 'wrap',
}: {
  display?: 'block' | 'flex' | 'inline-block' | 'inline-flex';
  hideSeparator?: boolean;
  maximumTargets?: number;
  size?: DeviceTargetPropSize;
  variant?: VariantProp;
  targets: DeviceTargetProps<Tag>[];
  overflow?: {
    as?: React.ElementType;
    to?: To;
  };
} & WrapProps) {
  const id = useId();
  const css = merge(displayCSS(display), wrapCSS(wrap));

  return (
    <DeviceTargetsContainer css={css}>
      {targets.slice(0, maximumTargets ?? targets.length).map((target, index) => (
        <DeviceTargetsToken key={`${id}-${target['aria-label'] ?? target.label ?? index}`}>
          <DeviceTarget display={display} size={size} variant={variant} {...target} />
          {index < targets.length - 1 && !hideSeparator && <Text>,</Text>}
        </DeviceTargetsToken>
      ))}
      {maximumTargets && targets.length > maximumTargets && (
        <DeviceTargetsOverflow
          targets={targets}
          maximumTargets={maximumTargets}
          overflow={overflow}
        />
      )}
    </DeviceTargetsContainer>
  );
}
